// src/components/layout/AppLayout.tsx
import React from "react";
import { NavLink } from "react-router-dom";
import { ProjectSummary } from "../../types/dashboard";
import OnboardingBar from "../onboarding/OnboardingBar";
import { useOnboarding } from "../../context/OnboardingContext";
import { FloatingFeedbackButton } from "../feedback/FloatingFeedbackButton";
import { useAuth } from "../../auth/AuthContext";
import logoMark from "../../assets/logo-mark.svg";

interface AppLayoutProps {
    projects: ProjectSummary[];
    currentProjectId: string | null;
    onProjectChange: (projectId: string) => void;
    // optional hooks for creating a project
    onCreateProject?: () => void;
    isCreatingProject?: boolean;
    children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({
    projects,
    currentProjectId,
    onProjectChange,
    onCreateProject,
    isCreatingProject = false,
    children,
}) => {
    const hasProjects = projects && projects.length > 0;

    const { markStepComplete } = useOnboarding();
    const { user, signOut } = useAuth();

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const nextId = e.target.value;
        if (nextId) {
            onProjectChange(nextId);
            // Onboarding: project selected from ANY page (dashboard, sources, workbench)
            markStepComplete("project");
        }
    };

    return (
        <div className="min-h-screen bg-slate-950 text-slate-50">
            <div className="flex h-screen">
                {/* Sidebar */}
                <aside className="flex w-64 flex-col border-r border-slate-900 bg-slate-950/90">
                    <div className="flex items-center border-b border-slate-900 px-4 py-3.5">
                        <img src={logoMark} alt="ScopeShield" className="h-8 w-auto flex-shrink-0 mr-2.5" />
                        <span className="text-[15px] font-bold leading-none text-white">Scope</span>
                        <span className="text-[15px] font-bold leading-none text-amber-400">Shield</span>
                    </div>

                    {/* Nav */}
                    <nav className="mt-2 space-y-1 px-2">
                        <SidebarLink to="/dashboard" label="Dashboard" />
                        <SidebarLink to="/sources" label="Sources" />
                        <SidebarLink to="/workbench" label="Workbench" />
                        <SidebarLink to="/settings" label="Settings" />
                    </nav>

                    {/* More section */}
                    <div className="mt-6 px-4">
                        <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                            More
                        </div>
                        <div className="space-y-1">
                            <button className="w-full rounded-lg px-3 py-1.5 text-left text-xs text-slate-400 transition hover:bg-slate-900/80 hover:text-slate-100">
                                Help &amp; feedback
                            </button>
                        </div>
                    </div>
                </aside>

                {/* Main content */}
                <main className="flex flex-col flex-1 overflow-hidden bg-slate-950/80">
                    {/* Global top bar */}
                    <header className="flex flex-shrink-0 items-center justify-between border-b border-slate-900 bg-slate-950/90 px-6 py-2">
                        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                            Active project
                        </div>
                        <div className="flex items-center gap-3">
                            {/* Project selector */}
                            <div className="flex items-center gap-2">
                                <label
                                    htmlFor="project-select"
                                    className="text-[11px] text-slate-400"
                                >
                                    Project:
                                </label>
                                <select
                                    id="project-select"
                                    value={currentProjectId ?? ""}
                                    onChange={handleSelectChange}
                                    className="min-w-[220px] rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs text-slate-100 shadow-sm outline-none transition focus:border-amber-500 focus:ring-1 focus:ring-amber-500 disabled:cursor-not-allowed disabled:opacity-60"
                                    disabled={!hasProjects}
                                >
                                    {!hasProjects && (
                                        <option value="">
                                            No projects yet
                                        </option>
                                    )}
                                    {hasProjects && !currentProjectId && (
                                        <option value="">
                                            Select a project…
                                        </option>
                                    )}
                                    {projects.map((project) => (
                                        <option key={project.id} value={project.id}>
                                            {project.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* New project button (optional) */}
                            {onCreateProject && (
                                <button
                                    type="button"
                                    onClick={onCreateProject}
                                    disabled={isCreatingProject}
                                    className={[
                                        "inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-semibold",
                                        isCreatingProject
                                            ? "bg-slate-800 text-slate-500 cursor-not-allowed"
                                            : "bg-amber-500 text-slate-900 hover:bg-amber-400",
                                    ].join(" ")}
                                >
                                    {isCreatingProject ? "Creating…" : "New project"}
                                </button>
                            )}

                            {/* User info and logout */}
                            <div className="flex items-center gap-2 border-l border-slate-800 pl-3">
                                <span className="text-xs text-slate-400">
                                    {user?.email}
                                </span>
                                <button
                                    type="button"
                                    onClick={signOut}
                                    className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-400 transition hover:bg-slate-900 hover:text-slate-100"
                                >
                                    Logout
                                </button>
                            </div>
                        </div>
                    </header>

                    {/* Top onboarding bar */}
                    <OnboardingBar />

                    <div className="flex flex-1 min-h-0 overflow-hidden">
                        <div className="flex-1 overflow-auto">
                            {children}
                        </div>
                    </div>
                </main>
            </div>

            {/* Floating Feedback Button */}
            <FloatingFeedbackButton />
        </div>
    );
};

interface SidebarLinkProps {
    to: string;
    label: string;
}

const SidebarLink: React.FC<SidebarLinkProps> = ({ to, label }) => {
    return (
        <NavLink
            to={to}
            className={({ isActive }) =>
                [
                    "flex items-center rounded-xl px-4 py-2 text-sm transition",
                    isActive
                        ? "bg-slate-800 text-slate-50 shadow-inner shadow-slate-900/60"
                        : "text-slate-400 hover:bg-slate-900 hover:text-slate-100",
                ].join(" ")
            }
        >
            {label}
        </NavLink>
    );
};

export default AppLayout;
export { AppLayout };
